import { CodeExtension, CodeIndentExtension } from "@lexical/code-core";
import { HorizontalRuleExtension } from "@lexical/extension";
import { HistoryExtension } from "@lexical/history";
import { LinkExtension } from "@lexical/link";
import { CheckListExtension, ListExtension } from "@lexical/list";
import { RichTextExtension } from "@lexical/rich-text";
import {
  configExtension,
  defineExtension,
  type AnyLexicalExtension,
  type AnyLexicalExtensionArgument,
} from "lexical";
import { BlockReorderExtension } from "../features/block-drag/extension";
import { BlockIdExtension } from "../features/block-id/extension";
import { BlockSelectionExtension } from "../features/block-selection/extension";
import { ClipboardExtension } from "../features/clipboard/extension";
import { CodeHighlightExtension } from "../features/code-highlight/extension";
import { ComposerSubmitExtension } from "../features/composer/extension";
import {
  CodeBlockExitExtension,
  CodeFenceCloseExtension,
  EmptyBlockBackspaceExtension,
  QuoteEnterExtension,
} from "../features/formatting/extension";
import { HashtagsExtension } from "../features/hashtags/extension";
import { LinkPolicyExtension } from "../features/links/extension";
import { ListTabExtension } from "../features/lists/extension";
import { TaskItemShortcutExtension } from "../features/lists/task-shortcut";
import { MarkdownShortcutsExtension } from "../features/markdown/extension";
import { TokensExtension } from "../features/tokens/extension";
import type { EditorPreset } from "../index";
import { EDITOR_THEME } from "./theme";

// Every preset registers every Increment A node type. A preset narrows shortcuts and UI, never
// what can be loaded: opening a document in a smaller preset must not normalize content away.
const CONTENT: AnyLexicalExtensionArgument[] = [
  configExtension(RichTextExtension, {
    // Typing past the end of an inline code span (or arrowing/clicking out of it) drops the code
    // format, so a completed `code` shortcut does not trap the caret.
    escapeFormatTriggers: {
      code: { onlyAtBoundary: true, enter: true, click: true, arrow: true, space: false },
    },
  }),
  HistoryExtension,
  ListExtension,
  CheckListExtension,
  LinkExtension,
  LinkPolicyExtension,
  CodeExtension,
  CodeIndentExtension,
  CodeFenceCloseExtension,
  CodeHighlightExtension,
  HorizontalRuleExtension,
  ListTabExtension,
  TaskItemShortcutExtension,
  EmptyBlockBackspaceExtension,
  QuoteEnterExtension,
  BlockIdExtension,
  BlockSelectionExtension,
  BlockReorderExtension,
  TokensExtension,
  HashtagsExtension,
  ClipboardExtension,
];

// Module-level constants: LexicalExtensionComposer rebuilds the editor whenever the root extension
// object changes identity, so presets are never created during render.
const DOCUMENT: AnyLexicalExtension = defineExtension({
  name: "z-editor/preset/document",
  theme: EDITOR_THEME,
  dependencies: [
    ...CONTENT,
    CodeBlockExitExtension,
    configExtension(MarkdownShortcutsExtension, { preset: "document" }),
  ],
});

const DESCRIPTION: AnyLexicalExtension = defineExtension({
  name: "z-editor/preset/description",
  theme: EDITOR_THEME,
  dependencies: [
    ...CONTENT,
    CodeBlockExitExtension,
    configExtension(MarkdownShortcutsExtension, { preset: "description" }),
  ],
});

const COMPOSER: AnyLexicalExtension = defineExtension({
  name: "z-editor/preset/composer",
  theme: EDITOR_THEME,
  dependencies: [
    ...CONTENT,
    configExtension(MarkdownShortcutsExtension, { preset: "composer" }),
    ComposerSubmitExtension,
  ],
});

export const PRESET_EXTENSIONS: Readonly<Record<EditorPreset, AnyLexicalExtension>> = {
  document: DOCUMENT,
  description: DESCRIPTION,
  composer: COMPOSER,
};
